
interface WordCountArcProps {
    current: number;
    target: number;
    momentum?: number; // 0-100
}

export function WordCountArc({ current, target, momentum = 0 }: WordCountArcProps) {
    const percentage = target > 0 ? Math.min((current / target) * 100, 100) : 0;
    const radius = 16;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;

    const wpm = Math.round((momentum / 100) * 80); // Estimate WPM based on momentum
    const wordsLeft = Math.max(0, target - current);
    const minutesLeft = wpm > 0 ? Math.ceil(wordsLeft / wpm) : null;

    return (
        <div className="group relative flex flex-col items-center">
            <div className="w-12 h-12 relative flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90">
                    {/* Background Track */}
                    <circle
                        cx="24"
                        cy="24"
                        r={radius}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        className="opacity-10"
                    />
                    {/* Progress Arc */}
                    <circle
                        cx="24"
                        cy="24"
                        r={radius}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3.5"
                        strokeDasharray={circumference}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        className="text-sage transition-all duration-1000 ease-out"
                        style={{
                            filter: percentage > 0 ? 'drop-shadow(0 0 3px currentColor)' : 'none'
                        }}
                    />
                    {/* Center Dot */}
                    <circle
                        cx="24"
                        cy="24"
                        r={1.5}
                        fill="currentColor"
                        className="opacity-40"
                    />
                </svg>
            </div>

            {/* Modern Predictive Tooltip */}
            <div className="absolute bottom-14 left-1/2 -translate-x-1/2 bg-black/90 text-white text-[10px] px-3 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-all pointer-events-none whitespace-nowrap backdrop-blur-md shadow-2xl border border-white/10 flex flex-col gap-1 items-center z-50">
                <div className="flex gap-4">
                    <div className="flex flex-col items-center">
                        <span className="opacity-50 text-[8px] uppercase tracking-tighter">Goal</span>
                        <span className="font-bold">{percentage.toFixed(0)}%</span>
                        <span className="opacity-40">{current.toLocaleString()}/{target.toLocaleString()}</span>
                    </div>
                    {minutesLeft !== null && minutesLeft < 1000 && (
                        <div className="flex flex-col items-center border-l border-white/10 pl-4">
                            <span className="opacity-50 text-[8px] uppercase tracking-tighter">Velocity</span>
                            <span className="font-bold text-sage">{minutesLeft}m</span>
                            <span className="opacity-40">until goal</span>
                        </div>
                    )}
                </div>
                {wpm > 5 && (
                    <div className="mt-1 pt-1 border-t border-white/5 w-full text-center opacity-60">
                        Writing at ~{wpm} WPM
                    </div>
                )}
            </div>
        </div>
    );
}
